import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trophy, FileDown, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { exportClubRankingsPdf } from "@/lib/export";

const CATEGORIES = [
  { key: "attendance", title: "Mejor asistencia", field: "attendance_pct", unit: "%", emoji: "🥇", route: "detalle-asistencia" },
  { key: "minutes", title: "Más minutos jugados", field: "minutes", unit: "'", emoji: "⚡", route: "detalle-partidos", qmetric: "minutes" },
  { key: "minutes_pct_total", title: "% min sobre total equipo", field: "minutes_pct_total", unit: "%", emoji: "🎯", route: "detalle-partidos", qmetric: "minutes" },
  { key: "matches_called", title: "Más convocatorias", field: "matches_called", unit: "", emoji: "📣", route: "detalle-partidos", qmetric: "called" },
  { key: "scorers", title: "Máximos goleadores", field: "goals", unit: "", emoji: "⚽" },
  { key: "cards", title: "Más tarjetas", field: "cards", unit: "", emoji: "🟨" },
];

const medals = ["bg-amber-400", "bg-slate-300", "bg-orange-400"];

export default function RankingsCard({ dateFrom, dateTo }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo);
    api.get(`/rankings/club?${params.toString()}`)
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  const shareWhatsApp = () => {
    if (!data) return;
    const lines = ["🏆 *Ranking del club E.F. Concepción Madrid* 🏆"];
    if (dateFrom || dateTo) {
      lines.push(`📅 ${dateFrom || "—"} → ${dateTo || "—"}`);
    }
    const medalsTxt = ["🥇", "🥈", "🥉"];
    CATEGORIES.forEach((cat) => {
      const rows = (data.rankings?.[cat.key] || []).slice(0, 3);
      if (rows.length === 0) return;
      lines.push("", `${cat.emoji} *${cat.title}*`);
      rows.forEach((r, i) => {
        const val = cat.key === "cards"
          ? `${r.yellow}🟨 ${r.red}🟥`
          : `${r[cat.field]}${cat.unit}`;
        lines.push(`${medalsTxt[i] || `${i + 1}.`} ${r.player_name} (${r.team_name}): ${val}`);
      });
    });
    const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="rankings-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-amber-500" />
          <CardTitle className="text-base font-black" style={{ fontFamily: "Nunito" }}>
            Ranking del club
          </CardTitle>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={shareWhatsApp}
            disabled={!data || loading}
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="share-rankings-whatsapp"
          >
            <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportClubRankingsPdf({ date_from: dateFrom, date_to: dateTo })}
            data-testid="export-rankings-pdf"
          >
            <FileDown className="h-4 w-4 mr-2" /> PDF
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Calculando rankings...
          </div>
        ) : !data ? (
          <div className="text-sm text-slate-400 italic py-4">Sin datos.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {CATEGORIES.map((cat) => {
              const rows = data.rankings?.[cat.key] || [];
              return (
                <div key={cat.key} className="space-y-1" data-testid={`ranking-${cat.key}`}>
                  <div className="text-xs font-bold text-slate-700 uppercase tracking-widest">
                    {cat.title}
                  </div>
                  {rows.length === 0 ? (
                    <div className="text-xs text-slate-400 italic py-2">
                      Sin datos en este rango.
                    </div>
                  ) : (
                    <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md">
                      {rows.slice(0, 5).map((r, i) => {
                        const clickable = cat.route && r.player_id;
                        const open = () => clickable && navigate(
                          `/players/${r.player_id}/${cat.route}?scope=no_pre${cat.qmetric ? `&metric=${cat.qmetric}` : ""}` +
                          `${dateFrom ? `&from=${dateFrom}` : ""}`);
                        return (
                        <li
                          key={`${cat.key}-${r.player_id || r.player_name}-${i}`}
                          className={`flex items-center gap-3 p-2 ${clickable ? "cursor-pointer hover:bg-purple-50" : ""}`}
                          onClick={open}
                          data-testid={clickable ? `ranking-${cat.key}-item-${i}` : undefined}
                        >
                          <span
                            className={`w-6 h-6 rounded-full grid place-items-center text-xs font-bold ${
                              medals[i] || "bg-slate-100 text-slate-500"
                            } ${i < 3 ? "text-white" : ""}`}
                          >
                            {i + 1}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold truncate">
                              {r.player_name}
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 truncate">
                              {r.team_name}
                            </div>
                          </div>
                          <span className="text-sm font-black text-purple-700">
                            {cat.key === "cards" ? (
                              <span className="flex items-center gap-1" data-testid={`cards-breakdown-${i}`}>
                                <span className="text-amber-500">{r.yellow}</span>
                                <span className="inline-block w-2.5 h-3.5 bg-amber-400 rounded-[2px]" />
                                <span className="text-red-600 ml-1">{r.red}</span>
                                <span className="inline-block w-2.5 h-3.5 bg-red-500 rounded-[2px]" />
                              </span>
                            ) : (
                              <>
                                {r[cat.field]}
                                {cat.unit}
                              </>
                            )}
                          </span>
                        </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
